import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Flag, CheckCircle2, Circle, Clock } from "lucide-react";
import { toast } from "sonner";

const MS_STATUS = {
  planned: { label: "Planned", icon: Circle, color: "#94A3B8" },
  in_progress: { label: "In Progress", icon: Clock, color: "#F59E0B" },
  completed: { label: "Completed", icon: CheckCircle2, color: "#10B981" },
};

export default function Milestones() {
  const { user } = useAuth();
  const isStaff = user.role !== "client";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(isStaff ? "" : user.client_id);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", status: "planned", progress: 0, due_date: "" });

  useEffect(() => {
    if (isStaff) api.get("/clients").then((r) => { setClients(r.data); if (r.data[0]) setClientId(r.data[0].id); });
  }, [isStaff]);

  const load = useCallback((cid) => {
    const q = cid ? `?client_id=${cid}` : "";
    api.get(`/milestones${q}`).then((r) => setItems(r.data));
  }, []);
  useEffect(() => { if (clientId || !isStaff) load(clientId); }, [clientId, isStaff, load]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      await api.post("/milestones", { ...form, client_id: clientId, due_date: form.due_date || null });
      toast.success("Milestone created");
      setOpen(false);
      setForm({ title: "", description: "", status: "planned", progress: 0, due_date: "" });
      load(clientId);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const updateProgress = async (m, progress) => {
    const status = progress >= 100 ? "completed" : progress > 0 ? "in_progress" : "planned";
    await api.patch(`/milestones/${m.id}`, { progress, status });
    load(clientId);
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Milestones</h1>
          <p className="text-slate-500 text-sm mt-1">Delivery roadmap & progress tracking</p>
        </div>
        <div className="flex items-center gap-2">
          {isStaff && (
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="w-48" data-testid="milestone-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {isStaff && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="new-milestone-btn"><Plus size={16} className="mr-1" /> New</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display">New milestone</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Title</Label>
                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="milestone-title-input" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Description</Label>
                    <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={2} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(MS_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Due date</Label>
                      <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Progress: {form.progress}%</Label>
                    <Slider value={[form.progress]} onValueChange={([v]) => setForm({ ...form, progress: v })} max={100} step={5} className="mt-3" /></div>
                </div>
                <DialogFooter><Button onClick={create} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="submit-milestone-btn">Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="relative pl-6">
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" />
        <div className="space-y-4">
          {items.length === 0 && <p className="text-sm text-slate-400 py-8">No milestones yet.</p>}
          {items.map((m) => {
            const S = MS_STATUS[m.status] || MS_STATUS.planned;
            return (
              <div key={m.id} className="relative" data-testid="milestone-card">
                <span className="absolute -left-6 top-4 h-3.5 w-3.5 rounded-full border-2 border-white" style={{ background: S.color }} />
                <Card className="p-5 border-slate-200 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <S.icon size={16} style={{ color: S.color }} />
                        <h3 className="font-display font-semibold text-slate-900">{m.title}</h3>
                      </div>
                      {m.description && <p className="text-sm text-slate-500 mt-1">{m.description}</p>}
                    </div>
                    <span className="text-xs text-slate-400 shrink-0">Due {fmtDate(m.due_date)}</span>
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                      <span style={{ color: S.color }}>{S.label}</span><span>{m.progress}%</span>
                    </div>
                    <Progress value={m.progress} className="h-2" />
                  </div>
                  {isStaff && (
                    <div className="flex gap-2 mt-3">
                      {[0, 25, 50, 75, 100].map((p) => (
                        <button key={p} onClick={() => updateProgress(m, p)} data-testid={`set-progress-${p}`}
                          className="text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 text-slate-600">{p}%</button>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
